import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type {
  UseRegisterSwimmerFormProps,
  FormSchema,
} from "./RegisterSwimmerForm.types";
import { formSchema } from "./RegisterSwimmerForm.types";
import type { SwimmerBatchItem } from "../../../../api/client/types/swimmer.types";
import {
  evaluateBatchDomain,
  findAgeClassification,
  getAdultAndMinorUnder12Indices,
  isAdultAC,
  isMinorAC,
} from "./swimmerBatchDomain";
import { useEffect, useMemo } from "react";
import { useWatch } from "react-hook-form";
import toast from "react-hot-toast";
import { getAgeClassificationDisplayLabel } from "../../utils/ageClassificationUtils";

export const useRegisterSwimmerForm = ({
  onSubmit,
  ageClassifications,
  initialSwimmer,
}: UseRegisterSwimmerFormProps) => {
  /* ------------------------ Age Classification Options ---------------------- */
  const ageClassificationsOptions = useMemo(
    () =>
      ageClassifications.map((ageClassification) => ({
        label: getAgeClassificationDisplayLabel(ageClassification.name),
        value: ageClassification.id.toString(),
      })),
    [ageClassifications]
  );

  /* ------------------------------- Form Setup ------------------------------ */
  const form = useForm<FormSchema>({
    mode: "onSubmit",
    resolver: zodResolver(formSchema),
    defaultValues: {
      swimmers: initialSwimmer
        ? [
            {
              name: initialSwimmer.name || "",
              building_tower: initialSwimmer.building_tower || "",
              building_apartment: initialSwimmer.building_apartment || "",
              age_classification_id:
                initialSwimmer.age_classification?.id?.toString() ||
                initialSwimmer.age_classification_id?.toString() ||
                "",
              swimmer_type_id:
                initialSwimmer.swimmer_type?.id?.toString() ||
                initialSwimmer.swimmer_type_id?.toString() ||
                "",
              responsibleIdx: "",
            },
          ]
        : [
            {
              name: "",
              building_tower: "",
              building_apartment: "",
              age_classification_id: "",
              swimmer_type_id: "",
              responsibleIdx: "",
            },
          ],
    },
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    getValues,
    formState: { errors, isValid },
    setError,
    clearErrors,
  } = form;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "swimmers",
  });

  const watchedSwimmers = watch("swimmers");
  // Cleaner per-swimmer watcher
  const watchedEachSwimmer = useWatch({
    control,
    name: "swimmers",
  });
  // --- PER-SWIMMER ERROR CLEANUP ---
  useEffect(() => {
    if (!watchedEachSwimmer) return;

    watchedEachSwimmer.forEach((swimmer, idx) => {
      const assigned = swimmer.responsibleIdx;

      // If no responsible selected, just clear error and continue
      if (!assigned || assigned === "") {
        clearErrors(`swimmers.${idx}.responsibleIdx`);
        return;
      }

      const responsibleIdx = parseInt(assigned, 10);
      const responsible = watchedEachSwimmer[responsibleIdx];

      // --- CASE 1: Assigned responsible was removed (index does not exist) ---
      if (!responsible) {
        // Reset the select
        form.setValue(`swimmers.${idx}.responsibleIdx`, "");
        clearErrors(`swimmers.${idx}.responsibleIdx`);
        return;
      }

      const ac = findAgeClassification(
        ageClassifications,
        responsible.age_classification_id
      );
      const stillAdult = ac && isAdultAC(ac.name);

      // --- CASE 2: Responsible changed age classification (no longer adult) ---
      if (!stillAdult) {
        form.setValue(`swimmers.${idx}.responsibleIdx`, "");
        clearErrors(`swimmers.${idx}.responsibleIdx`);
        return;
      }

      // If responsible is still valid → just clear any stale errors
      clearErrors(`swimmers.${idx}.responsibleIdx`);
    });

    // Clear global error — domain logic will re-add it only if needed
    clearErrors("root.global");
  }, [watchedEachSwimmer, ageClassifications, clearErrors, form]);

  /* --- Auto-assign single adult as responsible for all minors under 12 --- */
  useEffect(() => {
    if (!watchedEachSwimmer || watchedEachSwimmer.length === 0) return;

    const allHaveAge = watchedEachSwimmer.every(
      (s) => s?.age_classification_id && s.age_classification_id !== ""
    );
    if (!allHaveAge) return;

    const { adultIndices, minorUnder12Indices } =
      getAdultAndMinorUnder12Indices(watchedEachSwimmer, ageClassifications);

    const exactlyOneAdult = adultIndices.length === 1;
    const hasMinors = minorUnder12Indices.length > 0;
    if (!exactlyOneAdult || !hasMinors) return;

    const theOnlyAdultIdx = adultIndices[0];
    const expectedValue = theOnlyAdultIdx.toString();

    minorUnder12Indices.forEach((minorIdx) => {
      const current = watchedEachSwimmer[minorIdx]?.responsibleIdx?.trim() ?? "";
      if (current === expectedValue) return;

      const parsed = current === "" ? NaN : parseInt(current, 10);
      const pointsToValidAdult =
        !isNaN(parsed) &&
        parsed >= 0 &&
        parsed < watchedEachSwimmer.length &&
        adultIndices.includes(parsed);
      if (pointsToValidAdult) return;

      form.setValue(`swimmers.${minorIdx}.responsibleIdx`, expectedValue);
    });
  }, [watchedEachSwimmer, ageClassifications, form]);

  useEffect(() => {
    if (!watchedSwimmers || watchedSwimmers.length === 0) return;

    // Do not evaluate domain logic while age classification is missing
    const allHaveAge = watchedSwimmers.every(
      (s) => s.age_classification_id && s.age_classification_id !== ""
    );

    if (!allHaveAge) {
      clearErrors("root.global");
      return;
    }

    const { errors: domainErrors, canSubmit } = evaluateBatchDomain(
      watchedSwimmers,
      ageClassifications
    );

    // Clear global error if fixed
    if (canSubmit) {
      clearErrors("root.global");
    }

    // Clear swimmer-level domain errors
    domainErrors.swimmers.forEach((err, idx) => {
      if (!err.missingResponsible && !err.invalidResponsible) {
        clearErrors(`swimmers.${idx}.responsibleIdx`);
      }
    });
  }, [watchedSwimmers, ageClassifications, clearErrors]);

  /* ---------------------- Helpers: Age Classification Logic ---------------------- */

  const isMinorUnder12 = (id: string): boolean => {
    return isMinorAC(findAgeClassification(ageClassifications, id)?.name ?? "");
  };

  /* ---------------------- Responsible Dropdown Options --------------------- */
  const getAdultOptions = (currentIndex: number) => {
    return watchedSwimmers
      .map((swimmer, idx) => {
        if (idx === currentIndex) return null;

        const ac = findAgeClassification(
          ageClassifications,
          swimmer.age_classification_id
        );
        if (!ac || !isAdultAC(ac.name)) return null;

        return {
          value: idx.toString(),
          label: swimmer.name || `Bañista ${idx + 1}`,
        };
      })
      .filter(
        (option): option is { value: string; label: string } => option !== null
      );
  };

  /* ------------------------------- Misc Helpers ------------------------------ */
  const isPreloadedSwimmer = (index: number): boolean =>
    !!initialSwimmer && index === 0;

  /* ----------------------------- Add / Remove Rows ----------------------------- */
  const addSwimmer = () => {
    const first = getValues("swimmers.0");
    append({
      name: "",
      building_tower: first?.building_tower ?? "",
      building_apartment: first?.building_apartment ?? "",
      age_classification_id: "",
      swimmer_type_id: "",
      responsibleIdx: "",
    });
  };

  const removeSwimmer = (index: number) => {
    if (!isPreloadedSwimmer(index) && fields.length > 1) remove(index);
  };

  /* ------------------------------- Submit Logic ------------------------------ */
  const onFormSubmit = (data: FormSchema) => {
    const { errors: domainErrors, canSubmit } = evaluateBatchDomain(
      data.swimmers,
      ageClassifications
    );
    if (!canSubmit) {
      domainErrors.swimmers.forEach((swimmerErr, idx) => {
        if (swimmerErr.missingResponsible) {
          setError(`swimmers.${idx}.responsibleIdx`, {
            type: "manual",
            message: swimmerErr.missingResponsible,
          });
        }
        if (swimmerErr.invalidResponsible) {
          setError(`swimmers.${idx}.responsibleIdx`, {
            type: "manual",
            message: swimmerErr.invalidResponsible,
          });
        }
      });

      if (domainErrors.global) {
        setError("root.global", {
          type: "manual",
          message: domainErrors.global,
        });
      }
      toast.error("El formulario no es válido");

      return;
    }

    // If there's an initial swimmer, only submit new swimmers (skip index 0)
    const swimmersToSubmit = initialSwimmer
      ? data.swimmers.slice(1) // Skip the first swimmer (preloaded one)
      : data.swimmers; // Submit all swimmers if no preloaded swimmer

    // Create a map of form indices to submission indices for responsibleIdx calculation
    // Form indices: 0 (preloaded), 1, 2, 3...
    // Submission indices: 0, 1, 2... (preloaded is skipped)
    const formIndexToSubmissionIndex = new Map<number, number>();
    if (initialSwimmer) {
      // Map form indices to submission indices (skip index 0)
      data.swimmers.forEach((_, formIndex) => {
        if (formIndex > 0) {
          formIndexToSubmissionIndex.set(formIndex, formIndex - 1);
        }
      });
    } else {
      // No preloaded swimmer, indices match
      data.swimmers.forEach((_, formIndex) => {
        formIndexToSubmissionIndex.set(formIndex, formIndex);
      });
    }

    const payload = swimmersToSubmit.map((swimmer, submissionIndex) => {
      // Determine responsible reference - EXCLUSIVE: only one can be set
      // If responsibleIdx points to the preloaded swimmer (index 0), use responsible_id
      // Otherwise, use responsibleIdx with the correct submission index
      let responsibleIdx: number | undefined = undefined;
      let responsible_id: string | undefined = undefined;

      if (swimmer.responsibleIdx && swimmer.responsibleIdx !== "") {
        const responsibleFormIndex = parseInt(swimmer.responsibleIdx, 10);

        // Check if the responsible is the preloaded swimmer (form index 0)
        if (initialSwimmer && responsibleFormIndex === 0) {
          // Use the preloaded swimmer's ID - DO NOT set responsibleIdx
          responsible_id = initialSwimmer.id;
        } else {
          // Map the form index to submission index
          const responsibleSubmissionIndex =
            formIndexToSubmissionIndex.get(responsibleFormIndex);
          if (responsibleSubmissionIndex !== undefined) {
            responsibleIdx = responsibleSubmissionIndex;
          }
          // Ensure responsible_id is not set when using responsibleIdx
          responsible_id = undefined;
        }
      }

      // Build item with explicit exclusion - only include the one that is set
      const item: SwimmerBatchItem = {
        idx: submissionIndex,
        name: swimmer.name,
        building_tower: swimmer.building_tower,
        building_apartment: swimmer.building_apartment,
        age_classification_id: Number(swimmer.age_classification_id),
        swimmer_type_id: Number(swimmer.swimmer_type_id),
      };

      // EXCLUSIVE: Only add one or the other, never both
      if (responsible_id) {
        item.responsible_id = responsible_id;
      } else if (responsibleIdx !== undefined) {
        item.responsibleIdx = responsibleIdx;
      }

      return item;
    });

    // Only submit if there are new swimmers to add
    if (payload.length > 0) {
      onSubmit(payload);
    }
  };

  /* ------------------------------- Return API ------------------------------- */
  return {
    register,
    handleSubmit: handleSubmit(onFormSubmit),
    control,
    errors,
    isValid,
    fields,
    watchedSwimmers,
    ageClassificationsOptions,
    getAdultOptions,
    isMinorUnder12,
    addSwimmer,
    removeSwimmer,
    isPreloadedSwimmer,
    hasPreloadedSwimmer: !!initialSwimmer,
  };
};
